import type { AuthUser } from '@financas/shared';

/**
 * Cliente HTTP.
 *
 * O access token vive SO nesta memoria — nunca em localStorage, que qualquer
 * XSS leria. Ao recarregar a pagina ele some, e a sessao e reconstruida pelo
 * refresh em cookie httpOnly, que o JavaScript nao alcanca.
 *
 * Um 401 por token vencido dispara um unico refresh; as chamadas concorrentes
 * esperam a mesma promessa, em vez de cada uma abrir sua propria rotacao — o
 * que, com rotacao e deteccao de reuso, derrubaria a sessao inteira.
 */

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:3040';

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

export type AssetKind = 'REAL_ESTATE' | 'VEHICLE' | 'INVESTMENT' | 'RECEIVABLE' | 'OTHER';

export interface CategorySuggestions {
  available: boolean;
  unavailableReason: string | null;
  analyzed: number;
  suggestions: Array<{
    transactionId: string;
    description: string;
    amountCents: string;
    counterpartyName: string | null;
    categoryId: string;
    categoryName: string;
    colorToken: string;
    reason: string;
  }>;
}

/**
 * A leitura do mes.
 *
 * `facts` sao numeros do sistema; `reading` e o texto da IA sobre eles. Vem
 * separados porque a tela mostra os fatos mesmo quando o texto nao veio — e
 * porque nenhum numero desta tela passa pelo modelo.
 */
export interface Insights {
  month: string;
  facts: Array<{
    id: string;
    tipo: 'RESULTADO' | 'MAIOR_CATEGORIA' | 'SUBIU' | 'ASSINATURAS' | 'MAIOR_GASTO' | 'REPETIDO';
    titulo: string;
    valorCents: string;
    sobre: string | null;
    contagem: number | null;
    percentual: number | null;
  }>;
  reading: Array<{ factId: string; texto: string }>;
  aiAvailable: boolean;
  aiUnavailableReason: string | null;
  generatedAt: string;
}

/** Os lancamentos por tras de um quadro da leitura, e os totais deles. */
export interface InsightsDetail {
  factId: string;
  titulo: string;
  valorCents: string;
  explicacao: string;
  transactions: Transaction[];
  totais: {
    entradasCents: string;
    saidasCents: string;
    liquidoCents: string;
    quantidade: number;
  };
}

export interface Asset {
  id: string;
  name: string;
  kind: AssetKind;
  colorToken: string;
  notes: string | null;
  valueCents: string;
  debtCents: string;
  /** Valor menos financiamento. E o que entra no patrimonio. */
  netCents: string;
  valuedOn: string;
  includeInNetWorth: boolean;
  archived: boolean;
  sortOrder: number;
  valuationCount: number;
}

export interface AssetsSummary {
  assets: Asset[];
  grossCents: string;
  debtCents: string;
  netCents: string;
}

export interface AssetValuation {
  id: string;
  valueCents: string;
  debtCents: string;
  netCents: string;
  valuedOn: string;
  source: string | null;
  notes: string | null;
}

export interface NetWorth {
  totalCents: string;
  accountsCents: string;
  investmentsCents: string;
  creditCardDebtCents: string;
  assetsCents: string;
  assetDebtCents: string;
  composition: Array<{
    key: string;
    label: string;
    colorToken: string;
    amountCents: string;
    share: number;
  }>;
}

/** De onde saiu um dado que o sistema preencheu sozinho. */
export type DataSource = 'BANK' | 'RULE' | 'AI' | 'USER' | 'LEARNED' | 'DERIVED' | 'DEFAULT';

export interface Payees {
  referenceMonth: string;
  totalCents: string;
  payees: Array<{
    name: string;
    kind: 'MERCHANT' | 'PERSON' | 'COMPANY' | null;
    document: string | null;
    totalCents: string;
    count: number;
    lastDate: string;
    categoryName: string | null;
    colorToken: string | null;
  }>;
  unidentified: { count: number; totalCents: string };
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.details = body?.error?.details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Interno: impede laco infinito de refresh. */
  retrying?: boolean;
  signal?: AbortSignal;
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    // Necessario para o cookie de refresh atravessar a origem do Vite.
    credentials: 'include',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await rawRequest(path, options);

  if (response.status === 401 && !options.retrying && path !== '/api/v1/auth/refresh') {
    const renewed = await refreshSession();
    if (renewed) return apiRequest<T>(path, { ...options, retrying: true });

    onSessionLost?.();
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(response.status, body, `Falha na requisicao (${response.status}).`);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Baixa um arquivo servido pela API.
 *
 * Nao da para usar um `<a href>` simples: a rota exige o cabecalho de
 * autorizacao, e o navegador nao o manda numa navegacao comum. Entao busca-se
 * o conteudo com o token, transforma-se num blob e clica-se num link
 * temporario — que e como se entrega um arquivo protegido no navegador.
 *
 * Passa pelo mesmo caminho de renovacao de sessao do resto: um token vencido
 * no meio do ano fiscal nao deve virar um arquivo vazio.
 */
export async function baixarArquivo(path: string, options: RequestOptions = {}): Promise<void> {
  const response = await rawRequest(path, options);

  if (response.status === 401 && !options.retrying) {
    const renewed = await refreshSession();
    if (renewed) return baixarArquivo(path, { ...options, retrying: true });
    onSessionLost?.();
  }

  if (!response.ok) {
    throw new ApiError(response.status, null, `Nao consegui gerar o arquivo (${response.status}).`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeDoAnexo(response.headers.get('Content-Disposition'));
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Sem isto o blob fica preso na memoria da aba ate a pagina recarregar.
  URL.revokeObjectURL(url);
}

/** O nome que o servidor escolheu, ou um decente se ele nao disse. */
function nomeDoAnexo(header: string | null): string {
  const achado = header?.match(/filename="([^"]+)"/);
  return achado?.[1] ?? 'arquivo.csv';
}

export interface AuthenticatedResponse {
  status: 'authenticated';
  accessToken: string;
  accessTokenExpiresIn: number;
  user: AuthUser;
}

export type LoginResult = AuthenticatedResponse | { status: 'totp_required' };

/** Rotaciona o refresh. Concorrentes aguardam a mesma promessa. */
export function refreshSession(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await rawRequest('/api/v1/auth/refresh', { method: 'POST', body: {} });
      if (!response.ok) return false;

      const data = (await response.json()) as AuthenticatedResponse;
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Libera a proxima tentativa apenas depois de esta concluir.
      queueMicrotask(() => {
        refreshPromise = null;
      });
    }
  })();

  return refreshPromise;
}

/**
 * Envio de arquivo. O `Content-Type` NAO e definido aqui de proposito: quem
 * monta o cabecalho com o boundary correto e o proprio navegador, e escrever
 * "multipart/form-data" na mao quebra o corpo silenciosamente.
 */
export async function apiUpload<T>(path: string, form: FormData, retrying = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: form,
  });

  if (response.status === 401 && !retrying) {
    const renewed = await refreshSession();
    if (renewed) return apiUpload<T>(path, form, true);
    onSessionLost?.();
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(response.status, body, `Falha no envio (${response.status}).`);
  }

  return (await response.json()) as T;
}

// ------------------------------------------------------------------ rotas

export const api = {
  bootstrap: (payload: { name: string; email: string; password: string; householdName: string }) =>
    apiRequest<AuthenticatedResponse>('/api/v1/auth/bootstrap', { method: 'POST', body: payload }),

  acceptInvite: (payload: { token: string; name: string; password: string }) =>
    apiRequest<AuthenticatedResponse>('/api/v1/auth/invites/accept', {
      method: 'POST',
      body: payload,
    }),

  login: (payload: { email: string; password: string; totpCode?: string; recoveryCode?: string }) =>
    apiRequest<LoginResult>('/api/v1/auth/login', { method: 'POST', body: payload }),

  logout: () => apiRequest<{ ok: true }>('/api/v1/auth/logout', { method: 'POST', body: {} }),

  me: () => apiRequest<AuthUser>('/api/v1/auth/me'),

  household: () =>
    apiRequest<{
      id: string;
      name: string;
      timezone: string;
      currency: string;
      memberCount: number;
    }>('/api/v1/household'),

  members: () =>
    apiRequest<{
      members: Array<{
        userId: string;
        name: string;
        email: string;
        role: string;
        totpEnabled: boolean;
        joinedAt: string;
        isYou: boolean;
      }>;
    }>('/api/v1/household/members'),

  sessions: () =>
    apiRequest<{
      sessions: Array<{
        id: string;
        device: string | null;
        userAgent: string | null;
        ipAddress: string | null;
        createdAt: string;
        lastUsedAt: string;
        expiresAt: string;
        current: boolean;
      }>;
    }>('/api/v1/auth/sessions'),

  revokeSession: (id: string) =>
    apiRequest<{ ok: true; revoked: number }>(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' }),

  totpSetup: () =>
    apiRequest<{ secret: string; otpauthUrl: string; qrCodeDataUrl: string }>(
      '/api/v1/auth/totp/setup',
      { method: 'POST', body: {} },
    ),

  totpConfirm: (code: string) =>
    apiRequest<{ recoveryCodes: string[] }>('/api/v1/auth/totp/confirm', {
      method: 'POST',
      body: { code },
    }),

  regenerateRecoveryCodes: () =>
    apiRequest<{ recoveryCodes: string[] }>('/api/v1/auth/totp/recovery-codes', {
      method: 'POST',
      body: {},
    }),

  changePassword: (payload: {
    currentPassword: string;
    newPassword: string;
    revokeOtherSessions: boolean;
  }) =>
    apiRequest<{ ok: true; revokedSessions: number }>('/api/v1/auth/password', {
      method: 'POST',
      body: payload,
    }),

  createInvite: (payload: { email: string; role: 'ADMIN' | 'MEMBER' | 'VIEWER' }) =>
    apiRequest<{ id: string; email: string; role: string; token: string; expiresAt: string }>(
      '/api/v1/household/invites',
      { method: 'POST', body: payload },
    ),

  invites: () =>
    apiRequest<{
      invites: Array<{
        id: string;
        email: string;
        role: string;
        expiresAt: string;
        createdAt: string;
        status: string;
      }>;
    }>('/api/v1/household/invites'),

  revokeInvite: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/household/invites/${id}`, { method: 'DELETE' }),

  // ------------------------------------------------------------- financas

  institutions: () => apiRequest<{ institutions: Institution[] }>('/api/v1/institutions'),

  accounts: (includeArchived = false) =>
    apiRequest<AccountListResponse>(`/api/v1/accounts?includeArchived=${includeArchived}`),

  createAccount: (payload: Record<string, unknown>) =>
    apiRequest<Account>('/api/v1/accounts', { method: 'POST', body: payload }),

  updateAccount: (id: string, payload: Record<string, unknown>) =>
    apiRequest<Account>(`/api/v1/accounts/${id}`, { method: 'PATCH', body: payload }),

  deleteAccount: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/accounts/${id}`, { method: 'DELETE' }),

  categories: (kind?: 'INCOME' | 'EXPENSE') =>
    apiRequest<{ categories: CategoryNode[] }>(`/api/v1/categories${kind ? `?kind=${kind}` : ''}`),

  createCategory: (payload: Record<string, unknown>) =>
    apiRequest<{ id: string; name: string }>('/api/v1/categories', {
      method: 'POST',
      body: payload,
    }),

  updateCategory: (id: string, payload: Record<string, unknown>) =>
    apiRequest<{ ok: true }>(`/api/v1/categories/${id}`, { method: 'PATCH', body: payload }),

  deleteCategory: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/categories/${id}`, { method: 'DELETE' }),

  /** Inclui as arquivadas: a tela de categorias precisa mostrar as duas. */
  allCategories: () =>
    apiRequest<{ categories: CategoryNode[] }>('/api/v1/categories?includeArchived=true'),

  tags: () => apiRequest<{ tags: Tag[] }>('/api/v1/tags'),

  createTag: (payload: { name: string; colorToken?: string }) =>
    apiRequest<Tag>('/api/v1/tags', { method: 'POST', body: payload }),

  transactions: (query: string) =>
    apiRequest<TransactionListResponse>(`/api/v1/transactions${query ? `?${query}` : ''}`),

  createTransaction: (payload: Record<string, unknown>) =>
    apiRequest<{ transactions: Transaction[] }>('/api/v1/transactions', {
      method: 'POST',
      body: payload,
    }),

  updateTransaction: (id: string, payload: Record<string, unknown>) =>
    apiRequest<TransactionUpdated>(`/api/v1/transactions/${id}`, {
      method: 'PATCH',
      body: payload,
    }),

  /** O que chegou do banco e ninguem olhou ainda. */
  aConferir: () =>
    apiRequest<{ total: number; transactions: Transaction[]; conferidasHoje: Transaction[] }>(
      '/api/v1/transactions/a-conferir',
    ),

  conferir: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/transactions/${id}/conferir`, { method: 'POST' }),

  conferirTudo: () =>
    apiRequest<{ ok: true; conferidos: number }>('/api/v1/transactions/conferir-tudo', {
      method: 'POST',
    }),

  registrarWebhook: (credentialId: string) =>
    apiRequest<{ ok: true; jaExistia: boolean; webhookId: string; url: string }>(
      '/api/v1/openfinance/webhook/registrar',
      { method: 'POST', body: { credentialId } },
    ),

  deleteTransaction: (id: string, wholeGroup = false) =>
    apiRequest<{ ok: true; deleted: number }>(
      `/api/v1/transactions/${id}?wholeGroup=${wholeGroup}`,
      { method: 'DELETE' },
    ),

  createTransfer: (payload: Record<string, unknown>) =>
    apiRequest<{ transactions: Transaction[] }>('/api/v1/transfers', {
      method: 'POST',
      body: payload,
    }),

  invoices: (query = '') => apiRequest<{ invoices: Invoice[] }>(`/api/v1/invoices${query}`),

  payInvoice: (id: string, payload: Record<string, unknown>) =>
    apiRequest<{ transactions: Transaction[]; fullyPaid: boolean }>(`/api/v1/invoices/${id}/pay`, {
      method: 'POST',
      body: payload,
    }),

  dashboard: (month?: string) =>
    apiRequest<Dashboard>(`/api/v1/dashboard${month ? `?month=${month}` : ''}`),

  // --------------------------------------------------------------------- IA

  suggestCategories: (limit?: number) =>
    apiRequest<CategorySuggestions>('/api/v1/ia/sugerir-categorias', {
      method: 'POST',
      body: limit === undefined ? {} : { limit },
    }),

  applySuggestions: (
    accepted: Array<{ transactionId: string; categoryId: string; reason?: string | null }>,
  ) =>
    apiRequest<{ applied: number }>('/api/v1/ia/aplicar-categorias', {
      method: 'POST',
      body: { accepted },
    }),

  insights: (month?: string, forcar = false) =>
    apiRequest<Insights>(
      `/api/v1/ia/leitura-do-mes?${new URLSearchParams({
        ...(month ? { month } : {}),
        ...(forcar ? { forcar: '1' } : {}),
      }).toString()}`,
    ),

  bancos: () => apiRequest<{ bancos: Banco[] }>('/api/v1/bancos'),

  projecao: () => apiRequest<Projecao>('/api/v1/projecao'),

  insightsDetail: (fato: string, month?: string) =>
    apiRequest<InsightsDetail>(
      `/api/v1/ia/leitura-do-mes/${fato}?${new URLSearchParams(month ? { month } : {}).toString()}`,
    ),

  // ------------------------------------------------------------ patrimonio

  netWorth: () => apiRequest<NetWorth>('/api/v1/patrimonio'),

  assets: () => apiRequest<AssetsSummary>('/api/v1/bens'),

  createAsset: (payload: {
    name: string;
    kind: AssetKind;
    colorToken?: string;
    notes?: string | null;
    valueCents: string;
    debtCents?: string;
    valuedOn?: string;
    includeInNetWorth?: boolean;
  }) => apiRequest<Asset>('/api/v1/bens', { method: 'POST', body: payload }),

  updateAsset: (
    id: string,
    payload: { name?: string; kind?: AssetKind; colorToken?: string; archived?: boolean },
  ) => apiRequest<Asset>(`/api/v1/bens/${id}`, { method: 'PATCH', body: payload }),

  revalueAsset: (
    id: string,
    payload: { valueCents: string; debtCents?: string; valuedOn?: string; source?: string | null },
  ) => apiRequest<Asset>(`/api/v1/bens/${id}/avaliacoes`, { method: 'POST', body: payload }),

  assetValuations: (id: string) => apiRequest<AssetValuation[]>(`/api/v1/bens/${id}/avaliacoes`),

  deleteAsset: (id: string) => apiRequest<void>(`/api/v1/bens/${id}`, { method: 'DELETE' }),

  /**
   * Para quem o dinheiro foi no mes.
   *
   * Com `categoryId`, so daquela categoria: e o que abre ao clicar numa
   * fatia da rosca no dashboard.
   */
  payees: (month?: string, categoryId?: string) => {
    const query = new URLSearchParams();
    if (month) query.set('month', month);
    if (categoryId) query.set('categoryId', categoryId);
    const texto = query.toString();
    return apiRequest<Payees>(`/api/v1/payees${texto ? `?${texto}` : ''}`);
  },

  // ------------------------------------------------------------ importacao

  csvProfiles: () => apiRequest<{ profiles: CsvProfile[] }>('/api/v1/imports/csv-profiles'),

  imports: () => apiRequest<{ batches: ImportBatch[] }>('/api/v1/imports'),

  importPreview: (id: string) => apiRequest<ImportPreview>(`/api/v1/imports/${id}`),

  /**
   * A ordem importa: os campos vao ANTES do arquivo.
   *
   * O @fastify/multipart entrega em `file.fields` apenas o que foi parseado
   * ate encontrar a parte do arquivo — mandar `accountId` depois faria o
   * servidor responder "informe a conta" com a conta preenchida na tela.
   */
  uploadStatement: (payload: { file: File; accountId: string; columns?: unknown }) => {
    const form = new FormData();
    form.append('accountId', payload.accountId);
    if (payload.columns) form.append('columns', JSON.stringify(payload.columns));
    form.append('file', payload.file);
    return apiUpload<ImportPreview>('/api/v1/imports', form);
  },

  commitImport: (id: string, decisions: ImportDecision[]) =>
    apiRequest<{ batch: ImportBatch; imported: number; reconciled: number; skipped: number }>(
      `/api/v1/imports/${id}/confirmar`,
      { method: 'POST', body: { decisions } },
    ),

  discardImport: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/imports/${id}`, { method: 'DELETE' }),

  // ------------------------------------------------------- contas a pagar

  readBoleto: (code: string) =>
    apiRequest<BoletoRead>('/api/v1/boletos/ler', { method: 'POST', body: { code } }),

  payables: (query = '') => apiRequest<PayableListResponse>(`/api/v1/payables${query}`),

  compromissos: (month?: string) =>
    apiRequest<MonthCommitments>(`/api/v1/compromissos/resumo${month ? `?month=${month}` : ''}`),

  conciliarCompromissos: () =>
    apiRequest<{ baixados: number }>('/api/v1/compromissos/conciliar', { method: 'POST' }),

  vhJuris: () =>
    apiRequest<{
      configurado: boolean;
      pendentes: number;
      total: number;
      conferir: Array<{
        id: string;
        description: string;
        quemPagou: string;
        paidOn: string | null;
        amountCents: string;
      }>;
    }>('/api/v1/integracoes/vh-juris'),

  sincronizarVhJuris: () =>
    apiRequest<VhJurisResult>('/api/v1/integracoes/vh-juris/sincronizar', { method: 'POST' }),

  fixarLancamento: (transactionId: string) =>
    apiRequest<FixarResult>(`/api/v1/compromissos/fixar/${transactionId}`, { method: 'POST' }),

  asaas: () => apiRequest<AsaasState>('/api/v1/integracoes/asaas'),

  conectarAsaas: (token: string, startDate: string) =>
    apiRequest<AsaasState>('/api/v1/integracoes/asaas', {
      method: 'POST',
      body: { token, startDate },
    }),

  desconectarAsaas: () =>
    apiRequest<{ ok: true }>('/api/v1/integracoes/asaas', { method: 'DELETE' }),

  sincronizarAsaas: () =>
    apiRequest<AsaasResult>('/api/v1/integracoes/asaas/sincronizar', { method: 'POST' }),

  payablesSummary: () => apiRequest<PayablesSummary>('/api/v1/payables/resumo'),

  createPayable: (payload: Record<string, unknown>) =>
    apiRequest<Payable>('/api/v1/payables', { method: 'POST', body: payload }),

  updatePayable: (id: string, payload: Record<string, unknown>) =>
    apiRequest<Payable>(`/api/v1/payables/${id}`, { method: 'PATCH', body: payload }),

  payPayable: (id: string, payload: Record<string, unknown>) =>
    apiRequest<Payable>(`/api/v1/payables/${id}/baixa`, { method: 'POST', body: payload }),

  unpayPayable: (id: string) =>
    apiRequest<Payable>(`/api/v1/payables/${id}/baixa`, { method: 'DELETE' }),

  cancelPayable: (id: string) =>
    apiRequest<Payable>(`/api/v1/payables/${id}/cancelar`, { method: 'POST', body: {} }),

  deletePayable: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/payables/${id}`, { method: 'DELETE' }),

  recurrences: () => apiRequest<{ recurrences: Recurrence[] }>('/api/v1/recurrences'),

  createRecurrence: (payload: Record<string, unknown>) =>
    apiRequest<Recurrence>('/api/v1/recurrences', { method: 'POST', body: payload }),

  updateRecurrence: (id: string, payload: Record<string, unknown>) =>
    apiRequest<Recurrence>(`/api/v1/recurrences/${id}`, { method: 'PATCH', body: payload }),

  deleteRecurrence: (id: string) =>
    apiRequest<{ ok: true; keptPayables: number }>(`/api/v1/recurrences/${id}`, {
      method: 'DELETE',
    }),

  generatePayables: (monthsAhead = 3) =>
    apiRequest<{ created: number; skipped: number }>('/api/v1/recurrences/gerar', {
      method: 'POST',
      body: { monthsAhead },
    }),

  // ------------------------------------------------ Open Finance (Fase 5)

  openFinanceCredentials: () =>
    apiRequest<{ credentials: OpenFinanceCredential[] }>('/api/v1/openfinance/credenciais'),

  createOpenFinanceCredential: (payload: {
    nickname: string;
    clientId: string;
    clientSecret: string;
  }) =>
    apiRequest<OpenFinanceCredential>('/api/v1/openfinance/credenciais', {
      method: 'POST',
      body: payload,
    }),

  deleteOpenFinanceCredential: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/openfinance/credenciais/${id}`, { method: 'DELETE' }),

  bankConnections: () =>
    apiRequest<{ connections: BankConnection[] }>('/api/v1/openfinance/conexoes'),

  createBankConnection: (payload: {
    credentialId: string;
    itemId: string;
    institutionName?: string;
  }) =>
    apiRequest<BankConnection>('/api/v1/openfinance/conexoes', { method: 'POST', body: payload }),

  deleteBankConnection: (id: string) =>
    apiRequest<{ ok: true }>(`/api/v1/openfinance/conexoes/${id}`, { method: 'DELETE' }),

  syncBankConnection: (id: string) =>
    apiRequest<{ run: SyncRun; connection: BankConnection }>(
      `/api/v1/openfinance/conexoes/${id}/sincronizar`,
      { method: 'POST', body: {} },
    ),

  /**
   * Le o historico inteiro de novo.
   *
   * A varredura normal so pede o que e novo. Quando o sistema passa a
   * guardar um campo que antes descartava, o que ja esta gravado nunca mais
   * e reencontrado — e isto o reencontra.
   */
  rereadBankHistory: (id: string) =>
    apiRequest<{ run: SyncRun; connection: BankConnection }>(
      `/api/v1/openfinance/conexoes/${id}/reler-historico`,
      { method: 'POST', body: {} },
    ),

  syncRuns: (id: string) =>
    apiRequest<{ runs: SyncRun[] }>(`/api/v1/openfinance/conexoes/${id}/varreduras`),

  unlinkTransfer: (transactionId: string) =>
    apiRequest<{ ok: true; unlinked: number }>(
      `/api/v1/transactions/${transactionId}/desfazer-transferencia`,
      { method: 'POST', body: {} },
    ),

  rules: () => apiRequest<{ rules: Rule[] }>('/api/v1/rules'),

  createRule: (payload: Record<string, unknown>) =>
    apiRequest<Rule>('/api/v1/rules', { method: 'POST', body: payload }),

  updateRule: (id: string, payload: Record<string, unknown>) =>
    apiRequest<Rule>(`/api/v1/rules/${id}`, { method: 'PATCH', body: payload }),

  deleteRule: (id: string) => apiRequest<{ ok: true }>(`/api/v1/rules/${id}`, { method: 'DELETE' }),

  applyRules: (payload: { onlyUncategorized?: boolean; dryRun?: boolean; accountId?: string }) =>
    apiRequest<{
      matched: number;
      updated: number;
      dryRun: boolean;
      limitReached: boolean;
    }>('/api/v1/rules/reaplicar', {
      method: 'POST',
      body: payload,
    }),
};

// --------------------------------------------------------------- contratos

export interface Institution {
  id: string;
  name: string;
  shortName: string | null;
  code: string | null;
  colorToken: string;
  accountCount: number;
}

export type AccountType =
  'CHECKING' | 'SAVINGS' | 'PAYMENT' | 'CASH' | 'INVESTMENT' | 'CREDIT_CARD';

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  institutionId: string | null;
  institutionName: string | null;
  colorToken: string;
  icon: string | null;
  initialBalanceCents: string;
  initialBalanceDate: string;
  currentBalanceCents: string;
  includeInNetWorth: boolean;
  creditLimitCents: string | null;
  availableCreditCents: string | null;
  closingDay: number | null;
  dueDay: number | null;
  paymentAccountId: string | null;
  archived: boolean;
  sortOrder: number;
  transactionCount: number;
}

/** O saldo de cada banco, apurado no servidor. */
export interface EventoProjetado {
  id: string;
  origem: 'COMPROMISSO' | 'FATURA';
  descricao: string;
  kind: 'INCOME' | 'EXPENSE';
  amountCents: string;
  atrasado: boolean;
}

export interface DiaProjetado {
  date: string;
  saldoCents: string;
  entradaCents: string;
  saidaCents: string;
  eventos: EventoProjetado[];
}

export interface Projecao {
  hoje: string;
  ate: string;
  saldoHojeCents: string;
  dias: DiaProjetado[];
  menorSaldo: { date: string; saldoCents: string };
  primeiroNegativo: { date: string; saldoCents: string } | null;
  totalEntradaCents: string;
  totalSaidaCents: string;
  atrasadoCents: string;
  atrasados: Array<{
    id: string;
    descricao: string;
    kind: 'INCOME' | 'EXPENSE';
    amountCents: string;
  }>;
  faturaAbertaCents: string;
}

export interface Banco {
  nome: string;
  institutionId: string | null;
  saldoCents: string;
  faturaCents: string;
  contas: Array<{
    id: string;
    name: string;
    type: AccountType;
    currentBalanceCents: string;
  }>;
}

export interface AccountListResponse {
  accounts: Account[];
  totals: { netWorthCents: string; availableCents: string; creditCardDebtCents: string };
}

export interface CategoryNode {
  id: string;
  name: string;
  kind: 'INCOME' | 'EXPENSE';
  parentId: string | null;
  colorToken: string;
  icon: string | null;
  isSystem: boolean;
  archived: boolean;
  sortOrder: number;
  children: CategoryNode[];
}

export interface Tag {
  id: string;
  name: string;
  colorToken: string;
  transactionCount: number;
}

export interface Transaction {
  id: string;
  accountId: string;
  accountName: string;
  date: string;
  amountCents: string;
  kind: 'INCOME' | 'EXPENSE' | 'TRANSFER';
  status: 'PENDING' | 'CLEARED';
  source: string;
  /** O texto cru do extrato. Fica visivel para conferir com a fatura. */
  description: string;
  /** A mesma coisa, legivel: "Anthropic* Claude Sub" vira "Assinatura Claude". */
  displayName: string;
  notes: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryPath: string | null;
  colorToken: string | null;
  /** Quem recebeu (saida) ou quem mandou (entrada). */
  counterpartyName: string | null;
  /** Ja mascarado pela API: o numero inteiro nunca chega aqui. */
  counterpartyDocument: string | null;
  counterpartyKind: 'MERCHANT' | 'PERSON' | 'COMPANY' | null;
  /** De onde veio cada dado: banco, regra, IA, voce, deduzido, ou o padrao. */
  counterpartySource: DataSource | null;
  categorySource: DataSource | null;
  categoryNote: string | null;
  paymentMethod: string | null;
  transferGroupId: string | null;
  transferCounterpartAccountName: string | null;
  cardInvoiceId: string | null;
  paidInvoiceId: string | null;
  purchaseGroupId: string | null;
  installmentNumber: number | null;
  installmentTotal: number | null;
  tags: Array<{ id: string; name: string; colorToken: string }>;
  splits: Array<{
    id: string;
    categoryId: string;
    categoryName: string;
    amountCents: string;
    notes: string | null;
  }>;
  attachmentCount: number;
}

export interface TransactionListResponse {
  transactions: Transaction[];
  nextCursor: string | null;
  totals: { incomeCents: string; expenseCents: string; netCents: string; count: number };
}

export interface Invoice {
  id: string;
  accountId: string;
  accountName: string;
  referenceMonth: string;
  closingDate: string;
  dueDate: string;
  status: 'OPEN' | 'CLOSED' | 'PAID';
  paidAt: string | null;
  totalCents: string;
  paidCents: string;
  transactionCount: number;
}

export interface Dashboard {
  referenceMonth: string;
  totalBalanceCents: string;
  netWorthCents: string;
  monthIncomeCents: string;
  monthExpenseCents: string;
  monthNetCents: string;
  creditCardDebtCents: string;
  topCategories: Array<{
    categoryId: string | null;
    name: string;
    colorToken: string;
    totalCents: string;
    share: number;
  }>;
  openInvoices: Array<{
    id: string;
    accountName: string;
    dueDate: string;
    totalCents: string;
    status: 'OPEN' | 'CLOSED' | 'PAID';
  }>;
  recentTransactions: Transaction[];
}

// -------------------------------------------------------- importacao (Fase 3)

export interface CsvProfile {
  id: string;
  label: string;
  hint: string;
}

export type ImportRowStatus = 'NEW' | 'DUPLICATE' | 'RECONCILE' | 'IGNORED';

export interface ImportRow {
  id: string;
  lineNumber: number;
  date: string;
  description: string;
  amountCents: string;
  kind: 'INCOME' | 'EXPENSE';
  status: ImportRowStatus;
  duplicateOfTransactionId: string | null;
  reconcileTransactionId: string | null;
  reconcileDescription: string | null;
  reconcileDate: string | null;
  reconcileScore: number | null;
  suggestedCategoryId: string | null;
  suggestedCategoryName: string | null;
  matchedRuleName: string | null;
  warning: string | null;
}

export interface ImportBatch {
  id: string;
  accountId: string;
  accountName: string;
  source: 'OFX' | 'CSV';
  filename: string;
  status: 'PREVIEW' | 'APPLIED' | 'DISCARDED';
  counts: {
    total: number;
    novos: number;
    duplicados: number;
    conciliar: number;
    ignorados: number;
  };
  periodStart: string | null;
  periodEnd: string | null;
  problems: string[];
  createdAt: string;
  appliedAt: string | null;
  importedCount: number;
  reconciledCount: number;
}

export interface ImportPreview {
  batch: ImportBatch;
  rows: ImportRow[];
}

export interface ImportDecision {
  rowId: string;
  action: 'IMPORT' | 'SKIP' | 'RECONCILE';
  categoryId?: string | null;
  reconcileTransactionId?: string;
}

/**
 * O que o sistema aprendeu com uma categorizacao.
 *
 * Vem nulo quase sempre. Quando vem preenchido, outros lancamentos mudaram de
 * categoria junto — e a tela tem de dizer isso, porque o efeito e maior que a
 * linha que a pessoa acabou de editar.
 */
export interface Aprendizado {
  chave: string;
  campo: 'COUNTERPARTY' | 'DESCRIPTION';
  categoryName: string;
  aplicados: number;
  atualizou: boolean;
  ruleId: string;
}

export interface TransactionUpdated extends Transaction {
  aprendizado: Aprendizado | null;
}

export type RuleField = 'DESCRIPTION' | 'COUNTERPARTY' | 'AMOUNT' | 'ACCOUNT';
export type RuleOperator =
  | 'CONTAINS'
  | 'EQUALS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'REGEX'
  | 'GREATER_THAN'
  | 'LESS_THAN'
  | 'BETWEEN';

export interface Rule {
  id: string;
  name: string;
  priority: number;
  enabled: boolean;
  kind: 'INCOME' | 'EXPENSE' | null;
  categoryId: string | null;
  categoryName: string | null;
  tags: Array<{ id: string; name: string; colorToken: string }>;
  conditions: Array<{
    field: RuleField;
    operator: RuleOperator;
    value: string | null;
    amountMinCents: string | null;
    amountMaxCents: string | null;
  }>;
  appliedCount: number;
  /** Nasceu de uma categorizacao sua, e nao de um formulario preenchido. */
  learned: boolean;
  learnedKey: string | null;
  createdAt: string;
}

// ------------------------------------------------- contas a pagar (Fase 4)

export interface BoletoRead {
  kind: 'BANCARIO' | 'ARRECADACAO';
  barcode: string;
  digitableLine: string;
  formattedLine: string;
  bankCode: string | null;
  bankName: string | null;
  dueDate: string | null;
  amountCents: string | null;
  segment: number | null;
  segmentName: string | null;
  dueDateNote: string | null;
  amountNote: string | null;
}

export interface Payable {
  id: string;
  description: string;
  dueDate: string;
  amountCents: string;
  status: 'OPEN' | 'PAID' | 'CANCELED';
  /** Entra na conta ou sai dela. */
  kind: 'INCOME' | 'EXPENSE';
  /** O que procurar no extrato para a baixa acontecer sozinha. */
  counterpartyMatch: string | null;
  overdue: boolean;
  daysUntilDue: number;
  categoryId: string | null;
  categoryName: string | null;
  accountId: string | null;
  accountName: string | null;
  notes: string | null;
  boletoLine: string | null;
  boletoFormattedLine: string | null;
  boletoBankName: string | null;
  remindDaysBefore: number;
  paidOn: string | null;
  paidAmountCents: string | null;
  paidTransactionId: string | null;
  recurrenceId: string | null;
  recurrenceDescription: string | null;
  createdAt: string;
}

/**
 * O que ja tem dono no mes, e o que sobra.
 *
 * Conta de caixa, e nao de competencia: as compras do cartao nao entram uma a
 * uma porque nao e assim que elas saem da conta — sai a fatura.
 */
export interface MonthCommitments {
  month: string;
  previstoEntradaCents: string;
  recebidoCents: string;
  aReceberCents: string;
  previstoSaidaCents: string;
  pagoCents: string;
  aPagarCents: string;
  atrasadoCents: string;
  faturaCents: string;
  disponivelCents: string;
  sobraCents: string;
}

export interface PayableListResponse {
  payables: Payable[];
  totals: {
    openCents: string;
    overdueCents: string;
    paidCents: string;
    count: number;
    overdueCount: number;
  };
}

/** O que aconteceu ao transformar um lancamento em serie mensal. */
export interface FixarResult {
  recurrence: Recurrence;
  /** Ja havia serie para o mesmo recebedor: nenhuma foi criada. */
  jaExistia: boolean;
  gerados: number;
}

/** A conta Asaas: ligada ou nao, e desde quando. */
export interface AsaasState {
  conectado: boolean;
  accountId: string | null;
  startDate: string | null;
  lastSyncedAt: string | null;
}

/** O que a leitura do Asaas fez desta vez. */
export interface AsaasResult {
  conectado: boolean;
  erro: string | null;
  recebimentos: number;
  repasses: number;
  pareados: number;
  baixados: number;
  diferencaCents: string;
}

/** O que a leitura dos honorarios do VH JURIS fez desta vez. */
export interface VhJurisResult {
  configurado: boolean;
  erro: string | null;
  novos: number;
  atualizados: number;
  removidos: number;
  baixados: number;
  pendentes: number;
}

export interface PayablesSummary {
  overdueCount: number;
  overdueCents: string;
  dueSoonCount: number;
  dueSoonCents: string;
  next: Array<{
    id: string;
    description: string;
    dueDate: string;
    amountCents: string;
    overdue: boolean;
  }>;
}

export interface Recurrence {
  id: string;
  description: string;
  frequency: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  amountCents: string;
  kind: 'INCOME' | 'EXPENSE';
  counterpartyMatch: string | null;
  startDate: string;
  endDate: string | null;
  occurrences: number | null;
  categoryId: string | null;
  categoryName: string | null;
  accountId: string | null;
  accountName: string | null;
  notes: string | null;
  remindDaysBefore: number;
  active: boolean;
  generatedCount: number;
  nextDueDate: string | null;
  createdAt: string;
}

// -------------------------------------------------- Open Finance (Fase 5)

export interface OpenFinanceCredential {
  id: string;
  nickname: string;
  /** Os quatro ultimos do clientId. O segredo nunca volta. */
  clientIdTail: string;
  ownerName: string | null;
  isMine: boolean;
  connectionCount: number;
  createdAt: string;
}

export interface SyncRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED';
  trigger: 'SCHEDULE' | 'MANUAL' | 'WEBHOOK';
  fetchedCount: number;
  createdCount: number;
  skippedCount: number;
  transferCount: number;
  unmappedCount: number;
  pagesExhausted: boolean;
  errorCode: string | null;
  errorText: string | null;
}

export interface BankConnection {
  id: string;
  itemId: string;
  institutionName: string;
  credentialId: string;
  credentialNickname: string;
  status: 'ACTIVE' | 'WAITING_USER' | 'CONSENT_EXPIRED' | 'ERROR';
  statusDetail: string | null;
  consentExpiresAt: string | null;
  consentDaysLeft: number | null;
  lastSyncedAt: string | null;
  accountCount: number;
  lastRun: SyncRun | null;
}
